import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { ActionButton } from '@/components/custom-ui/action-button';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';

interface ApplicationModalProps {
    needId: string;
    needTitle: string;
    onClose: () => void;
    onSuccess: () => void;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({ needId, needTitle, onClose, onSuccess }) => {
    const [coverLetter, setCoverLetter] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await jobApplicationApi.applyToNeed(needId, {
                cover_letter: coverLetter || undefined,
            });
            onSuccess();
        } catch (err: any) {
            setError(err?.message || 'Något gick fel. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Skicka ansökan</h2>
                        <p className="text-sm text-gray-500">{needTitle}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personligt brev (valfritt)
                    </label>
                    <textarea
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder="Berätta kort om dig själv och varför du är intresserad av denna tjänst..."
                        className="w-full h-32 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                    />

                    {error && (
                        <p className="mt-3 text-sm text-red-600">{error}</p>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl"
                    >
                        Avbryt
                    </button>
                    <ActionButton
                        icon={Send}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Skickar...' : 'Skicka ansökan'}
                    </ActionButton>
                </div>
        </IslandSlideOver>
    );
};

export default ApplicationModal;
